// TennisTrack Hub — Pannello Notifiche | 029 Project
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './PannelloNotifiche.css';

const PannelloNotifiche = () => {
  const [aperto, setAperto] = useState(false);
  const [notifiche, setNotifiche] = useState([]);
  const [nonLette, setNonLette] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Carica contatore notifiche (polling leggero ogni 60s)
  const aggiornaCount = useCallback(async () => {
    try {
      const res = await api.get('/notifiche/count');
      setNonLette(res.data.nonLette || 0);
    } catch {
      // silenzioso
    }
  }, []);

  useEffect(() => {
    aggiornaCount();
    const interval = setInterval(aggiornaCount, 60000); // ogni minuto
    return () => clearInterval(interval);
  }, [aggiornaCount]);

  // Carica lista notifiche quando si apre il pannello
  const apriPannello = async () => {
    setAperto(true);
    setLoading(true);
    try {
      const res = await api.get('/notifiche');
      setNotifiche(res.data.notifiche);
      setNonLette(res.data.nonLette);
    } catch {
      // silenzioso
    } finally {
      setLoading(false);
    }
  };

  // Chiudi clicking fuori
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setAperto(false);
      }
    };
    if (aperto) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aperto]);

  const segnaLetta = async (id) => {
    try {
      await api.patch(`/notifiche/${id}/leggi`);
      setNotifiche(prev => prev.map(n => n.id === id ? { ...n, letta: true } : n));
      setNonLette(prev => Math.max(0, prev - 1));
    } catch {
      // silenzioso
    }
  };

  const segnaLetteTutte = async () => {
    try {
      await api.post('/notifiche/leggi-tutte');
      setNotifiche(prev => prev.map(n => ({ ...n, letta: true })));
      setNonLette(0);
    } catch {
      // silenzioso
    }
  };

  const eliminaNotifica = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifiche/${id}`);
      setNotifiche(prev => prev.filter(n => n.id !== id));
    } catch {
      // silenzioso
    }
  };

  const clickNotifica = async (n) => {
    if (!n.letta) await segnaLetta(n.id);
    if (n.link) {
      navigate(n.link);
      setAperto(false);
    }
  };

  const icona = (tipo) => ({
    info: 'ℹ️',
    successo: '✅',
    avviso: '⚠️',
    template_approvato: '🎉',
    template_rifiutato: '❌',
    upgrade_premium: '⭐',
  }[tipo] || 'ℹ️');

  const formatData = (iso) => {
    const d = new Date(iso);
    const ora = new Date();
    const diffMs = ora - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffOre = Math.floor(diffMs / 3600000);
    const diffGg = Math.floor(diffMs / 86400000);
    if (diffMin < 2) return 'ora';
    if (diffMin < 60) return `${diffMin} min fa`;
    if (diffOre < 24) return `${diffOre}h fa`;
    if (diffGg < 7) return `${diffGg}gg fa`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="notifiche-wrapper" ref={panelRef}>
      {/* Campanella */}
      <button
        className={`notifiche-btn ${nonLette > 0 ? 'has-unread' : ''}`}
        onClick={() => aperto ? setAperto(false) : apriPannello()}
        title="Notifiche"
      >
        🔔
        {nonLette > 0 && (
          <span className="notifiche-badge">{nonLette > 9 ? '9+' : nonLette}</span>
        )}
      </button>

      {/* Pannello dropdown */}
      {aperto && (
        <div className="notifiche-panel">
          <div className="notifiche-header">
            <span className="notifiche-title">🔔 Notifiche</span>
            {nonLette > 0 && (
              <button className="btn-text-small" onClick={segnaLetteTutte}>
                Segna tutte lette
              </button>
            )}
          </div>

          <div className="notifiche-list">
            {loading ? (
              <div className="notifiche-empty">Caricamento...</div>
            ) : notifiche.length === 0 ? (
              <div className="notifiche-empty">
                <span style={{ fontSize: '1.8rem', display: 'block', marginBottom: '8px' }}>🔕</span>
                Nessuna notifica
              </div>
            ) : (
              notifiche.map(n => (
                <div
                  key={n.id}
                  className={`notifica-item ${!n.letta ? 'non-letta' : ''} ${n.link ? 'clickable' : ''}`}
                  onClick={() => clickNotifica(n)}
                >
                  <span className="notifica-icona">{icona(n.tipo)}</span>
                  <div className="notifica-body">
                    <div className="notifica-titolo">{n.titolo}</div>
                    {n.messaggio && (
                      <div className="notifica-msg">{n.messaggio}</div>
                    )}
                    <div className="notifica-data">{formatData(n.created_at)}</div>
                  </div>
                  <button
                    className="notifica-del"
                    onClick={(e) => eliminaNotifica(n.id, e)}
                    title="Elimina"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PannelloNotifiche;
